import { useState, useEffect } from 'react'
import { useProject } from '@/contexts/project-context'
import { SimulationEngine } from '@/lib/simulation-engine'
import { ScenarioType, ScenarioParameters, SimulationResults } from '@/types/simulation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { 
  UserPlus, 
  DollarSign, 
  TrendingUp, 
  Target,
  Zap,
  AlertCircle,
  Save
} from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'

interface SimulationWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (results: SimulationResults) => void
}

const scenarioOptions: Array<{ value: ScenarioType; label: string; icon: any; description: string }> = [
  {
    value: 'new_hire',
    label: 'New Hire',
    icon: UserPlus,
    description: 'Model the dilution impact of hiring a new team member'
  },
  {
    value: 'funding_round',
    label: 'Funding Round',
    icon: DollarSign,
    description: 'Simulate a new investment round and its effects'
  },
  {
    value: 'exit',
    label: 'Exit Scenario',
    icon: TrendingUp,
    description: 'Calculate payouts for an acquisition or IPO'
  },
  {
    value: 'milestone',
    label: 'Milestone Grants',
    icon: Target,
    description: 'Model equity grants triggered by milestones'
  },
  {
    value: 'option_exercise',
    label: 'Option Exercise',
    icon: Zap,
    description: 'Calculate the impact of exercising options'
  }
]

export default function SimulationWizard({ open, onOpenChange, onComplete }: SimulationWizardProps) {
  const { currentProject } = useProject()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [scenarioType, setScenarioType] = useState<ScenarioType | null>(null)
  const [parameters, setParameters] = useState<ScenarioParameters>({
    effective_date: new Date().toISOString().split('T')[0]
  })
  const [vestingSchedules, setVestingSchedules] = useState<any[]>([])
  const [equityClasses, setEquityClasses] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      if (!currentProject) return

      try {
        const [vestingData, classesData, usersData] = await Promise.all([
          supabase.from('vesting_schedules').select('*').order('duration_months'),
          supabase.from('equity_classes').select('*').order('id'),
          supabase.from('users').select('id, email, first_name, last_name').order('first_name')
        ])

        if (vestingData.error) {
          console.error('Error loading vesting schedules:', vestingData.error)
        } else {
          console.log('Loaded vesting schedules:', vestingData.data)
          setVestingSchedules(vestingData.data || [])
        }

        if (classesData.error) {
          console.error('Error loading equity classes:', classesData.error)
        } else {
          console.log('Loaded equity classes:', classesData.data)
          setEquityClasses(classesData.data || [])
        }

        if (usersData.error) {
          console.error('Error loading users:', usersData.error)
        } else {
          setUsers(usersData.data || [])
        }
      } catch (error) {
        console.error('Error loading reference data:', error)
        toast.error('Failed to load reference data')
      }
    }

    loadReferenceData()
  }, [currentProject])

  const handleScenarioSelect = (type: ScenarioType) => {
    setScenarioType(type)
    setStep(2)
  }

  const handleRunSimulation = async () => {
    if (!currentProject || !scenarioType) return

    setLoading(true)
    try {
      const engine = new SimulationEngine(currentProject.id)
      const results = await engine.runSimulation(scenarioType, parameters)
      
      onComplete(results)
      toast.success('Simulation completed successfully')
      onOpenChange(false)
      resetWizard()
    } catch (error) {
      console.error('Simulation error:', error)
      toast.error('Failed to run simulation')
    } finally {
      setLoading(false)
    }
  }

  const resetWizard = () => {
    setStep(1)
    setScenarioType(null)
    setParameters({ effective_date: new Date().toISOString().split('T')[0] })
  }

  const renderScenarioForm = () => {
    switch (scenarioType) {
      case 'new_hire':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input
                  value={parameters.new_hire?.name.split(' ')[0] || ''}
                  onChange={(e) => setParameters({
                    ...parameters,
                    new_hire: {
                      ...parameters.new_hire!,
                      name: e.target.value + ' ' + (parameters.new_hire?.name.split(' ')[1] || '')
                    }
                  })}
                  placeholder="John"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={parameters.new_hire?.name.split(' ')[1] || ''}
                  onChange={(e) => setParameters({
                    ...parameters,
                    new_hire: {
                      ...parameters.new_hire!,
                      name: (parameters.new_hire?.name.split(' ')[0] || '') + ' ' + e.target.value
                    }
                  })}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={parameters.new_hire?.email || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  new_hire: { ...parameters.new_hire!, email: e.target.value }
                })}
                placeholder="john.doe@example.com"
              />
            </div>

            <div>
              <Label>Role</Label>
              <Input
                value={parameters.new_hire?.role || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  new_hire: { ...parameters.new_hire!, role: e.target.value }
                })}
                placeholder="Senior Engineer"
              />
            </div>

            <div>
              <Label>Shares to Offer</Label>
              <Input
                type="number"
                value={parameters.new_hire?.shares_offered || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  new_hire: { 
                    ...parameters.new_hire!, 
                    shares_offered: parseInt(e.target.value) || 0 
                  }
                })}
                placeholder="50000"
              />
            </div>

            <div>
              <Label>Equity Class</Label>
              <Select
                value={parameters.new_hire?.equity_class_id?.toString() || ''}
                onValueChange={(value) => setParameters({
                  ...parameters,
                  new_hire: {
                    ...parameters.new_hire!,
                    equity_class_id: value
                  }
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={equityClasses.length === 0 ? "Loading equity classes..." : "Select equity class"} />
                </SelectTrigger>
                <SelectContent>
                  {equityClasses.length === 0 ? (
                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                  ) : (
                    equityClasses.map((ec) => (
                      <SelectItem key={ec.id} value={ec.id}>
                        {ec.class_name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Vesting Schedule</Label>
              <Select
                value={parameters.new_hire?.vesting_schedule_id || ''}
                onValueChange={(value) => setParameters({
                  ...parameters,
                  new_hire: {
                    ...parameters.new_hire!,
                    vesting_schedule_id: value
                  }
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={vestingSchedules.length === 0 ? "Loading vesting schedules..." : "Select vesting schedule"} />
                </SelectTrigger>
                <SelectContent>
                  {vestingSchedules.length === 0 ? (
                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                  ) : (
                    vestingSchedules.map((vs) => (
                      <SelectItem key={vs.id} value={vs.id}>
                        {vs.schedule_name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Annual Salary (Optional)</Label>
              <Input
                type="number"
                value={parameters.new_hire?.salary || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  new_hire: { 
                    ...parameters.new_hire!, 
                    salary: parseInt(e.target.value) || undefined 
                  }
                })}
                placeholder="150000"
              />
            </div>
          </div>
        )

      case 'funding_round':
        return (
          <div className="space-y-4">
            <div>
              <Label>Round Name</Label>
              <Input
                value={parameters.funding_round?.round_name || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  funding_round: { 
                    ...parameters.funding_round!, 
                    round_name: e.target.value 
                  }
                })}
                placeholder="Series A"
              />
            </div>

            <div>
              <Label>Pre-Money Valuation</Label>
              <Input
                type="number"
                value={parameters.funding_round?.pre_money_valuation || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  funding_round: { 
                    ...parameters.funding_round!, 
                    pre_money_valuation: parseInt(e.target.value) || 0 
                  }
                })}
                placeholder="10000000"
              />
            </div>

            <div>
              <Label>Investment Amount</Label>
              <Input
                type="number"
                value={parameters.funding_round?.investment_amount || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  funding_round: { 
                    ...parameters.funding_round!, 
                    investment_amount: parseInt(e.target.value) || 0 
                  }
                })}
                placeholder="2000000"
              />
            </div>

            <div>
              <Label>Equity Class for New Shares</Label>
              <Select
                value={parameters.funding_round?.equity_class_id?.toString() || ''}
                onValueChange={(value) => setParameters({
                  ...parameters,
                  funding_round: {
                    ...parameters.funding_round!,
                    equity_class_id: value
                  }
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={equityClasses.length === 0 ? "Loading equity classes..." : "Select equity class"} />
                </SelectTrigger>
                <SelectContent>
                  {equityClasses.length === 0 ? (
                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                  ) : (
                    equityClasses.filter(ec => ec.class_name.includes('Preferred')).map((ec) => (
                      <SelectItem key={ec.id} value={ec.id}>
                        {ec.class_name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Lead Investor Name</Label>
              <Input
                value={parameters.funding_round?.investors?.[0]?.name || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  funding_round: { 
                    ...parameters.funding_round!,
                    investors: [{
                      name: e.target.value,
                      investment_amount: parameters.funding_round?.investment_amount || 0,
                      is_lead: true
                    }]
                  }
                })}
                placeholder="Acme Ventures"
              />
            </div>
          </div>
        )

      case 'exit':
        return (
          <div className="space-y-4">
            <div>
              <Label>Exit Type</Label>
              <Select
                value={parameters.exit?.exit_type || ''}
                onValueChange={(value: 'acquisition' | 'ipo' | 'liquidation') => setParameters({
                  ...parameters,
                  exit: { 
                    ...parameters.exit!, 
                    exit_type: value 
                  }
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select exit type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="acquisition">Acquisition</SelectItem>
                  <SelectItem value="ipo">IPO</SelectItem>
                  <SelectItem value="liquidation">Liquidation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Exit Valuation</Label>
              <Input
                type="number"
                value={parameters.exit?.exit_valuation || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  exit: { 
                    ...parameters.exit!, 
                    exit_valuation: parseInt(e.target.value) || 0 
                  }
                })}
                placeholder="50000000"
              />
            </div>

            <div>
              <Label>Transaction Expenses</Label>
              <Input
                type="number"
                value={parameters.exit?.expenses || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  exit: { 
                    ...parameters.exit!, 
                    expenses: parseInt(e.target.value) || 0 
                  }
                })}
                placeholder="500000"
              />
            </div>

            <div>
              <Label>Escrow Percentage (Optional)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={parameters.exit?.escrow_percentage || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  exit: { 
                    ...parameters.exit!, 
                    escrow_percentage: parseInt(e.target.value) || undefined 
                  }
                })}
                placeholder="10"
              />
            </div>
          </div>
        )

      default:
        return <div>Scenario form not implemented yet</div>
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Choose a Simulation Scenario' : `Configure ${scenarioType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Scenario`}
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? 'Select a what-if scenario to model its impact on your cap table'
              : 'Enter the parameters for your simulation'
            }
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {scenarioOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleScenarioSelect(option.value)}
                className="flex flex-col items-start p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center mb-2">
                  <option.icon className="h-5 w-5 mr-2 text-gray-600" />
                  <h3 className="font-medium">{option.label}</h3>
                </div>
                <p className="text-sm text-gray-600">{option.description}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="py-4">
            <div className="mb-4">
              <Label>Effective Date</Label>
              <Input
                type="date"
                value={parameters.effective_date}
                onChange={(e) => setParameters({
                  ...parameters,
                  effective_date: e.target.value
                })}
              />
            </div>

            {renderScenarioForm()}

            <div className="mt-4">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={parameters.notes || ''}
                onChange={(e) => setParameters({
                  ...parameters,
                  notes: e.target.value
                })}
                placeholder="Add any additional context or assumptions..."
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              disabled={loading}
            >
              Back
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false)
              resetWizard()
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          {step === 2 && (
            <Button
              onClick={handleRunSimulation}
              disabled={loading || !scenarioType}
            >
              {loading ? 'Running...' : 'Run Simulation'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}